import { Suspense, lazy, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { configured, isNative } from './config.js';
import { signIn, signOut } from './auth/msal.js';
import { useMe } from './useMe.js';
import { chooseShell } from './shells/chooseShell.js';
import FieldShell from './shells/FieldShell.jsx';
import OfficeShell from './shells/OfficeShell.jsx';
import Home from './screens/Home.jsx';
import Placeholder from './screens/Placeholder.jsx';
import Support from './screens/Support.jsx';
import Profile from './screens/Profile.jsx';
import Inbox from './modules/comms/Inbox.jsx';
import Sent from './modules/comms/Sent.jsx';
// Writing a message carries the text editor: loaded only when the office opens it.
const Compose = lazy(() => import('./modules/comms/Compose.jsx'));
// Email signatures carry the drawing code and a GIF encoder: loaded when opened.
const Signatures = lazy(() => import('./modules/signatures/Signatures.jsx'));
import CommDetail from './modules/comms/CommDetail.jsx';
import Activity from './modules/comms/Activity.jsx';
import Permissions from './screens/Permissions.jsx';
import Department from './screens/Department.jsx';
import MatchUps from './modules/matchups/MatchUps.jsx';
import BookingsCalendar from './modules/bookings/BookingsCalendar.jsx';
import BookingsList from './modules/bookings/BookingsList.jsx';
import PlanTheDay from './modules/plans/PlanTheDay.jsx';
import Schedule from './modules/plans/Schedule.jsx';
import ScheduleJob from './modules/plans/ScheduleJob.jsx';
import Vans from './modules/fleet/Vans.jsx';
import BookingTypes from './modules/bookings/BookingTypes.jsx';
import AuditTrail from './screens/AuditTrail.jsx';
import DeleteBookings from './modules/bookings/DeleteBookings.jsx';
import Incidents from './modules/incidents/Incidents.jsx';
import Helpdesk from './modules/helpdesk/Helpdesk.jsx';
import NewTicket from './modules/helpdesk/NewTicket.jsx';
import Stock, { StockCount } from './modules/stock/Stock.jsx';
import StockCounts from './modules/stock/StockCounts.jsx';
import StockOrders from './modules/stock/StockOrders.jsx';
import JobStockLayout from './modules/stock/JobStockLayout.jsx';
import StockUsed from './modules/stock/StockUsed.jsx';
import { StockOrder, StockOrderForm } from './modules/stock/Orders.jsx';
import { SurveysPage, SurveyPage } from './modules/surveys/pick.jsx';
import Teams from './modules/teams/Teams.jsx';
import Whereabouts from './modules/whereabouts/Whereabouts.jsx';
import Pulse from './modules/pulse/Pulse.jsx';
import JobTimes from './modules/jobTimes/JobTimes.jsx';
import DefectsPage from './modules/bookings/DefectsPage.jsx';
import Tasks from './modules/tasks/Tasks.jsx';
import PartnerAccess from './screens/PartnerAccess.jsx';
import NativeBridge from './components/NativeBridge.jsx';
import { OFFICE_PHONE, OFFICE_TEL } from './contact.js';
import { PLAY_URL, reloadForUpdate, useAppUpdate, versionText } from './native/updates.js';
import { unGo } from './native/links.js';

const SHELL_KEY = 'nu.shell';

function readSaved() {
  try { return localStorage.getItem(SHELL_KEY); } catch { return null; }
}

export default function App() {
  const { accounts } = useMsal();
  const account = accounts[0];
  const { me, status } = useMe(account?.homeAccountId);
  const [saved, setSaved] = useState(readSaved);
  const update = useAppUpdate();

  if (update.updateFromPlay) {
    return (
      <Notice title="Update the app">
        <p className="notice-lead">This version of Nano Utilities is too old. Update it from Play to carry on.</p>
        <a className="btn primary" href={PLAY_URL}>Update from Play</a>
      </Notice>
    );
  }

  if (!configured) {
    return <Notice title="Not configured yet">Set VITE_ENTRA_TENANT_ID and VITE_ENTRA_CLIENT_ID (see web/.env.example).</Notice>;
  }
  if (!account) {
    return (
      <Notice title="Nano Utilities">
        <p className="notice-lead">Sign in with your Nano Utilities or Nano Fibre work account.</p>
        <button type="button" className="btn primary" onClick={signIn}>Sign in with Microsoft</button>
      </Notice>
    );
  }
  if (status === 'loading') return <Notice title="Loading…" />;
  if (status === 'no-access') {
    return (
      <Notice title="No access yet">
        Your account is signed in but has no Nano Utilities role. Ask IT to add you to the right NU- group.
        <button type="button" className="btn" onClick={signOut}>Sign out</button>
      </Notice>
    );
  }
  if (!me) {
    return (
      <Notice title={status === 'offline' ? 'No connection' : 'Something went wrong'}>
        The app needs a connection the first time you sign in on this device.
        <button type="button" className="btn" onClick={() => window.location.reload()}>Try again</button>
      </Notice>
    );
  }

  const shell = chooseShell({ capabilities: me.capabilities, native: isNative, saved });
  const both = me.capabilities.includes('field.use') && me.capabilities.includes('office.use');
  // A page's route exists only for people who can open it (Platform > Permissions).
  const page = (id) => me.capabilities.includes(`page.${id}.view`);
  const switchTo = (s) => {
    try { localStorage.setItem(SHELL_KEY, s); } catch { /* private mode: choice lasts this session only */ }
    setSaved(s);
  };
  const Shell = shell === 'field' ? FieldShell : OfficeShell;
  const other = shell === 'field' ? 'office' : 'field';
  const shellProps = both ? { onSwitchShell: () => switchTo(other) } : {};

  // NativeBridge comes after Routes so a waiting link wins over /auth's
  // redirect to home.
  return (
    <>
      <Routes>
        <Route element={<Shell me={me} {...shellProps} />}>
          <Route index element={<Home me={me} shell={shell} />} />
          <Route path="messages" element={<Inbox />} />
          <Route path="messages/:id" element={<Inbox />} />
          {me.capabilities.includes('office.use') && <Route path="tasks" element={<Tasks />} />}
          <Route path="support" element={<Support />} />
          <Route path="profile" element={<Profile me={me} />} />
          <Route path="helpdesk" element={<Helpdesk me={me} shell={shell} />} />
          <Route path="helpdesk/new" element={<NewTicket me={me} shell={shell} />} />
          <Route path="helpdesk/:id" element={<Helpdesk me={me} shell={shell} />} />
          <Route path="department/:id" element={<Department me={me} />} />
          {me.capabilities.includes('field.use') && (
            <>
              <Route path="schedule" element={<Schedule />} />
              <Route path="schedule/job/:id" element={<ScheduleJob />} />
              <Route path="stock" element={<Stock />} />
              <Route path="stock/weekly" element={<Navigate to="/stock/count" replace />} />
              <Route path="stock/count" element={<StockCount me={me} />} />
              <Route path="stock/order" element={<StockOrder me={me} />} />
              <Route path="stock/order/edit" element={<StockOrderForm me={me} />} />
            </>
          )}
          {me.capabilities.includes('bookings.view') && (
            <Route path="bookings" element={<Navigate to={me.capabilities.includes('page.bookings-calendar.view') ? '/bookings/calendar' : '/bookings/list'} replace />} />
          )}
          {page('bookings-calendar') && <Route path="bookings/calendar" element={<BookingsCalendar me={me} />} />}
          {page('bookings-list') && <Route path="bookings/list" element={<BookingsList me={me} />} />}
          {page('whereabouts') && <Route path="whereabouts" element={<Whereabouts />} />}
          {page('pulse') && <Route path="pulse" element={<Pulse />} />}
          {page('job-times') && <Route path="job-times" element={<JobTimes />} />}
          {page('defects') && <Route path="defects" element={<DefectsPage me={me} />} />}
          {page('plan') && <Route path="plan-the-day" element={<PlanTheDay me={me} />} />}
          {page('incidents') && <Route path="incidents" element={<Incidents />} />}
          {page('surveys') && (
            <>
              <Route path="surveys" element={<SurveysPage me={me} shell={shell} />} />
              <Route path="surveys/:id" element={<SurveyPage me={me} shell={shell} />} />
            </>
          )}
          {page('matchups') && <Route path="teams" element={<Teams me={me} />} />}
          {page('matchups') && <Route path="match-ups" element={<MatchUps me={me} />} />}
          {page('comms') && (
            <>
              <Route path="comms" element={<Sent />} />
              <Route path="comms/new" element={<Suspense fallback={<p className="muted">Loading…</p>}><Compose /></Suspense>} />
              <Route path="comms/:id" element={<CommDetail />} />
              <Route path="comms/:id/edit" element={<Suspense fallback={<p className="muted">Loading…</p>}><Compose /></Suspense>} />
            </>
          )}
          {page('comms-activity') && (
            <>
              <Route path="comms/people" element={<Activity />} />
              <Route path="comms/people/:personId" element={<Activity />} />
            </>
          )}
          {page('stock-counts') && <Route path="stock/counts" element={<StockCounts />} />}
          {page('stock-orders') && <Route path="stock/orders" element={<StockOrders />} />}
          {page('stock-job-used') && <Route path="stock/used" element={<StockUsed />} />}
          {page('stock-job-layout') && <Route path="stock/job-layout" element={<JobStockLayout me={me} />} />}
          {page('vans') && <Route path="fleet/vans" element={<Vans me={me} />} />}
          {me.capabilities.includes('platform.admin') && <Route path="platform/booking-types" element={<BookingTypes />} />}
          {me.capabilities.includes('platform.admin') && <Route path="platform/audit" element={<AuditTrail />} />}
          {me.capabilities.includes('platform.admin') && <Route path="platform/delete-bookings" element={<DeleteBookings />} />}
          {me.capabilities.includes('platform.admin') && <Route path="permissions" element={<Permissions me={me} />} />}
          {page('signatures') && <Route path="platform/signatures" element={<Suspense fallback={<p className="muted">Loading…</p>}><Signatures me={me} /></Suspense>} />}
          {me.capabilities.includes('platform.admin') && <Route path="platform/network-plus" element={<PartnerAccess />} />}
          <Route path="auth" element={<Navigate to="/" replace />} />
          <Route path="go/*" element={<GoLink />} />
          <Route path="*" element={<Placeholder title="Page not found" />} />
        </Route>
      </Routes>
      <NativeBridge />
      {update.webReady && (
        <div className="update-bar" role="status">
          <span>A new version of Nano Utilities is ready.</span>
          <button type="button" className="btn primary" onClick={reloadForUpdate}>Reload</button>
        </div>
      )}
    </>
  );
}

// https://nano.nanoutilities.co.uk/go/<screen> in a browser: that screen.
function NoticeVersion() {
  return versionText(useAppUpdate().native);
}

function GoLink() {
  const { pathname, search, hash } = useLocation();
  return <Navigate to={`${unGo(pathname)}${search}${hash}`} replace />;
}

function Notice({ title, children }) {
  return (
    <div className="notice">
      <div className="notice-card">
        <img className="logo" src="/brand/nano-utilities-badge.svg" alt="Nano Utilities" />
        <h1>{title}</h1>
        {children && <div className="notice-body">{children}</div>}
        <p className="notice-foot">
          Need help? <a href={OFFICE_TEL}>Call the office · {OFFICE_PHONE}</a>
          <span className="notice-version">Version <NoticeVersion /></span>
        </p>
      </div>
    </div>
  );
}
